import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import "pages/secure/facility/order/order.scss";
import Input from "components/form/input/Input";
import { useTranslation } from "react-i18next";

import RainChecksTable from "./RainChecksTable";
import { GetRainChecks, IRainCheck } from "api/rpc/2024-04/facilityAdmin/payment/rainCheck";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarsFilter, faXmark } from "@fortawesome/pro-regular-svg-icons";
import useDebounce from "hooks/events/useDebounce";
import RainCheckFilterDrawer, { IRaincheckFilters } from "./RainCheckFilterDrawer";
import { formatDate, valueToString } from "helpers/Helpers";
import moment from "moment";
import { LocaleCurrency } from "helpers/Locale";
import { useAppSelector } from "hooks/redux";

interface IState {
  rainchecks: IRainCheck[];
}

const TABLE_LIMIT = 25;

export default function Orders() {
  const { t } = useTranslation();
  const { facilityStore } = useAppSelector(store => store);
  const [raincheckFiltersDrawerOpen, setRaincheckFiltersDrawerOpen] = useState<boolean>(false);
  const [state, setState] = useState<IState>({
    rainchecks: undefined,
  });

  const [filterState, setFilterState] = useState<IRaincheckFilters>({
    search: "",
    offset: 0,
    start_date: null,
    end_date: null,
    status: null,
    max_balance: "",
    min_balance: "",
  });

  const debouncedSearch = useDebounce(
    filterState?.search,
    500,
  );

  // Search Rain Checks
  useEffect(() => {
    if (filterState === undefined) {
      return;
    }

    const source = axios.CancelToken.source();
    void loadRainChecks(source.token);

    return () => {
      source.cancel();
    };
  }, [debouncedSearch, filterState.offset, filterState?.start_date, filterState?.status, filterState?.min_balance, filterState?.max_balance]);

  function updateState(newState: Partial<IState>) {
    setState((cur: IState) => {
      return { ...cur, ...newState };
    });
  }

  async function loadRainChecks(token: CancelToken) {
    updateState({ rainchecks: undefined });

    const filters = buildFilters();

    const raincheckRes = await GetRainChecks(
      {
        filters: filters?.length > 0 ? filters : undefined,
        search: filterState.search ? filterState?.search : undefined,
        offset: filterState.offset,
        limit: TABLE_LIMIT,
      },
      false,
      token,
    );
    if (raincheckRes.status !== StatusCode.OK) {
      return [];
    }

    updateState({ rainchecks: raincheckRes.data });

    return raincheckRes.data;
  }

  function buildFilters() {
    const filters = [];
    if (filterState?.status && filterState?.status?.length > 0) {
      filters?.push({ label: "status", value: filterState?.status });
    }
    if (filterState?.min_balance !== "" && filterState?.min_balance != null && !isNaN(Number(filterState?.min_balance))) {
      filters?.push({ label: "min_balance", value: Number(filterState?.min_balance) });
    }
    if (filterState?.max_balance !== "" && filterState?.max_balance != null && !isNaN(Number(filterState?.max_balance))) {
      filters?.push({ label: "max_balance", value: Number(filterState?.max_balance) });
    }
    if (filterState?.start_date && filterState?.end_date) {
      filters?.push({ label: "start_date", value: formatDate(filterState?.start_date) }, { label: "end_date", value: formatDate(filterState?.end_date) });
    }
    if (filterState?.start_date && !filterState?.end_date) {
      filters?.push({ label: "start_date", value: formatDate(filterState?.start_date) }, { label: "end_date", value: formatDate(filterState?.start_date) });
    }

    return filters;
  }

  function getFilterText(key: keyof IRaincheckFilters) {
    if (key === "start_date") {
      return `${t("Date")}: ${moment(filterState[key])?.format("ll")} ${filterState?.end_date && filterState?.end_date !== filterState?.start_date ? `- ${moment(filterState?.end_date)?.format("ll")}` : ""}`;
    } else if (key === "status") {
      return `${t("Status")}: ${filterState[key]?.map(s => valueToString(s))?.join(", ")}`;
    } else if (key === "min_balance" || key === "max_balance") {
      return <div>{t("Balance: ")} {filterState?.min_balance ? <LocaleCurrency currency={facilityStore?.facility?.default_currency_code} amount={filterState?.min_balance} /> : null} {filterState?.min_balance && filterState?.max_balance ? " - " : null} {filterState?.max_balance ? <LocaleCurrency currency={facilityStore?.facility?.default_currency_code} amount={filterState?.max_balance} /> : null}</div>;
    }
    return key;
  }

  const filterApplied = filterState?.start_date
     || filterState?.max_balance || filterState?.min_balance || filterState?.status?.length > 0;

  return (
    <Page title={t("secure.facility.order.rain_checks.002")} narrow>
      <div className="flex gap-2 mb-4">
        <div className="flex-grow">
          <Input
            labelHidden
            value={filterState.search}
            onChange={e => setFilterState(prevState => ({ ...prevState, search: e.target.value }))}
            type="search"
            placeholder={t("secure.facility.order.rain_checks.003")}
          ></Input>
        </div>
        <div>
          <Button size="medium" type="secondary" onClick={() => setRaincheckFiltersDrawerOpen(prev => !prev)}>
            <FontAwesomeIcon icon={faBarsFilter} className="mr-2" />
            {t("Filter by")}</Button>
        </div>
      </div>
      {filterApplied ? (
        <>
          <div className="deposits-table-tag-container">
            {Object.keys(filterState)?.filter((key: keyof IRaincheckFilters) =>
              key !== "search" && key !== "offset" && filterState[key])?.map((key: keyof IRaincheckFilters, index) => {
                if (key === "max_balance" && filterState?.min_balance && filterState?.max_balance) {
                  return;
                }

                if (key === "end_date") {
                  return;
                }

                if (key === "start_date") {
                  return (
                    <div key={index} className="deposits-table-tag">
                      <p className="deposits-table-tag-text">{getFilterText(key)}</p>
                      <FontAwesomeIcon icon={faXmark} className="deposits-table-tag-x" onClick={() => {
                          setFilterState(prev => ({ ...prev, start_date: null, end_date: null }));
                      }} />
                    </div>
                  );
                }
                return (
                  <div key={index} className="deposits-table-tag">
                    <p className="deposits-table-tag-text">{getFilterText(key)}</p>
                    <FontAwesomeIcon icon={faXmark} className="deposits-table-tag-x" onClick={() => {
                      if ((key === "min_balance" || key === "max_balance") && (filterState?.min_balance && filterState?.max_balance)) {
                        setFilterState(prev => ({ ...prev, min_balance: "", max_balance: "" }));
                      } else {
                        setFilterState(prev => ({ ...prev, [key]: null }));
                      }
                    }} />
                  </div>
                );
              })}
            <Button size="small" type="text" onClick={() => {
              setFilterState(prev => ({
                ...prev,
                start_date: null,
                end_date: null,
                min_balance: "",
                max_balance: "",
                status: null,
              }));
            }}><FontAwesomeIcon icon={faXmark} className="mr-2" /> {t("Clear all")}</Button>
          </div>
          <br />
        </>
      ) : null}

      <RainChecksTable
        rainChecks={state.rainchecks}
        tableLimit={TABLE_LIMIT}
        tableOffset={filterState.offset}
        handleTableOffset={direction =>
          setFilterState(prev => ({
            ...prev,
            offset: direction === "prev" ? prev.offset - TABLE_LIMIT : prev.offset + TABLE_LIMIT,
          }))
        }
      />

      <RainCheckFilterDrawer
        open={raincheckFiltersDrawerOpen}
        onClose={() => setRaincheckFiltersDrawerOpen(false)}
        filters={filterState}
        onOk={filters => setFilterState(prev => ({ ...prev, ...filters }))}
      />
    </Page>
  );
}
